import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminMember, AdminGroup } from '@/lib/api'
import AssetImageField from '@/components/AssetImageField'

const ROLES = ['CENTER', 'LEADER', 'MEMBER', 'TRAINEE']

interface FormData {
  id: string
  group_id: string
  group_slug: string
  stage_name: string
  real_name: string
  role: string
  positions: string
  image_url: string
  birth_year: string
  debut_date: string
  is_active: boolean
}

const blank: FormData = {
  id: '', group_id: '', group_slug: '', stage_name: '', real_name: '',
  role: 'MEMBER', positions: '', image_url: '', birth_year: '', debut_date: '', is_active: true,
}

function toFormData(m: AdminMember): FormData {
  return {
    id: m.id, group_id: m.group_id, group_slug: m.group_slug, stage_name: m.stage_name,
    real_name: m.real_name ?? '', role: m.role, positions: m.positions.join(', '),
    image_url: m.image_url, birth_year: m.birth_year?.toString() ?? '',
    debut_date: m.debut_date ?? '', is_active: m.is_active,
  }
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-800">
        <h2 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{title}</h2>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  )
}

export default function MemberFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { token, isGroupManager, managedGroupSlug } = useAuth()

  const isEdit = Boolean(id)

  const [form, setForm] = useState<FormData>(blank)
  const [groups, setGroups] = useState<AdminGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!token) return

    const stateGroup = (location.state as { initialGroupSlug?: string } | null)?.initialGroupSlug
    const fetchGroups = api.getAdminGroups(token)

    if (isEdit) {
      const stateMember = (location.state as { member?: AdminMember } | null)?.member
      if (stateMember) {
        fetchGroups.then((groupList) => {
          setGroups(groupList)
          setForm(toFormData(stateMember))
          setLoading(false)
        })
      } else {
        Promise.all([fetchGroups, api.getAdminMembers(token)]).then(([groupList, members]) => {
          setGroups(groupList)
          const found = members.find((m) => m.id === id)
          if (found) setForm(toFormData(found))
          setLoading(false)
        })
      }
    } else {
      fetchGroups.then((groupList) => {
        setGroups(groupList)
        const slug = isGroupManager && managedGroupSlug ? managedGroupSlug : (stateGroup ?? '')
        if (slug) {
          const group = groupList.find((g) => g.slug === slug)
          if (group) setForm((prev) => ({ ...prev, group_slug: group.slug, group_id: group.id }))
        }
        setLoading(false)
      })
    }
  }, [id, isEdit, token, location.state, isGroupManager, managedGroupSlug])

  const set = (field: keyof FormData, value: string | boolean) =>
    setForm((prev) => ({ ...prev, [field]: value }))

  const handleGroupChange = (slug: string) => {
    const group = groups.find((g) => g.slug === slug)
    setForm((prev) => ({ ...prev, group_slug: slug, group_id: group?.id ?? '' }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return
    setSaving(true)
    try {
      const payload = {
        id: form.id,
        group_id: form.group_id,
        group_slug: form.group_slug,
        stage_name: form.stage_name,
        real_name: form.real_name || null,
        role: form.role,
        positions: form.positions.split(',').map((p) => p.trim()).filter(Boolean),
        image_url: form.image_url,
        birth_year: form.birth_year ? parseInt(form.birth_year) : null,
        debut_date: form.debut_date || null,
        is_active: form.is_active,
        display_order: 0,
      }

      if (isEdit && id) {
        const { id: _id, ...updatePayload } = payload
        void _id
        const updated = await api.updateMember(token, id, updatePayload)
        navigate(`/members/${id}`, { replace: true, state: { member: updated } })
      } else {
        await api.createMember(token, payload)
        navigate(-1)
      }
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const inp = 'w-full bg-zinc-800 text-white rounded-lg px-3 py-2 text-sm border border-zinc-700 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-600'
  const lbl = 'block text-xs text-zinc-400 mb-1.5'

  if (loading) {
    return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>
  }

  return (
    <div className="p-6">
      <form onSubmit={handleSubmit}>
        {/* Sticky top bar */}
        <div className="sticky top-0 z-10 bg-zinc-950 flex items-center justify-between pb-4 mb-6 border-b border-zinc-800">
          <div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors mb-3"
            >
              <ChevronLeft size={14} />
              Kembali
            </button>
            <h1 className="text-xl font-bold text-white">
              {isEdit ? 'Edit Member' : 'New Member'}
            </h1>
            {isEdit && <p className="text-zinc-500 text-sm mt-1">{form.stage_name}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-5 py-2 text-sm text-zinc-400 hover:text-white transition-colors rounded-lg hover:bg-zinc-800"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 text-sm font-semibold bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Menyimpan…' : isEdit ? 'Simpan Perubahan' : 'Buat Member'}
            </button>
          </div>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">

        {/* ── Informasi Dasar ─────────────────────────── */}
        <Section title="Informasi Dasar">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Stage Name <span className="text-red-400">*</span></label>
              <input
                type="text"
                value={form.stage_name}
                onChange={(e) => set('stage_name', e.target.value)}
                required
                placeholder="Nama panggung"
                className={inp}
              />
            </div>
            <div>
              <label className={lbl}>Real Name</label>
              <input
                type="text"
                value={form.real_name}
                onChange={(e) => set('real_name', e.target.value)}
                placeholder="Nama asli (opsional)"
                className={inp}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Group <span className="text-red-400">*</span></label>
              {isGroupManager ? (
                <input
                  type="text"
                  value={form.group_slug}
                  disabled
                  className={inp + ' opacity-40 cursor-not-allowed'}
                />
              ) : (
                <select
                  value={form.group_slug}
                  onChange={(e) => handleGroupChange(e.target.value)}
                  required
                  className={inp}
                >
                  <option value="">Pilih group…</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.slug}>{g.name}</option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className={lbl}>Role</label>
              <select
                value={form.role}
                onChange={(e) => set('role', e.target.value)}
                className={inp}
              >
                {ROLES.map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
        </Section>

        {/* ── Karir ───────────────────────────────────── */}
        <Section title="Karir">
          <div>
            <label className={lbl}>Posisi</label>
            <input
              type="text"
              value={form.positions}
              onChange={(e) => set('positions', e.target.value)}
              placeholder="VOCALIST, DANCER, RAPPER — pisahkan dengan koma"
              className={inp}
            />
            <p className="text-[11px] text-zinc-600 mt-1.5">Pisahkan beberapa posisi dengan koma</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Tanggal Debut</label>
              <input
                type="date"
                value={form.debut_date}
                onChange={(e) => set('debut_date', e.target.value)}
                className={inp + ' [color-scheme:dark]'}
              />
            </div>
            <div>
              <label className={lbl}>Tahun Lahir</label>
              <input
                type="number"
                value={form.birth_year}
                onChange={(e) => set('birth_year', e.target.value)}
                min="1990"
                max="2015"
                placeholder="YYYY"
                className={inp}
              />
            </div>
          </div>
        </Section>

        {/* ── Foto ────────────────────────────────────── */}
        <Section title="Foto">
          <AssetImageField
            value={form.image_url}
            onChange={(url) => set('image_url', url)}
            label="Foto Member"
            feature="members"
          />
        </Section>

        {/* ── Identitas (create only) ─────────────────── */}
        {!isEdit && (
          <Section title="Identitas">
            <div>
              <label className={lbl}>
                Member ID{' '}
                <span className="text-zinc-600 font-normal normal-case tracking-normal">— primary key</span>
              </label>
              <input
                type="text"
                value={form.id}
                onChange={(e) => set('id', e.target.value)}
                required
                placeholder="contoh: crescendo-hana"
                className={inp}
              />
              <p className="text-[11px] text-zinc-600 mt-1.5">
                Tidak dapat diubah setelah member dibuat. Gunakan format <span className="text-zinc-500">slug-nama</span>.
              </p>
            </div>
          </Section>
        )}

        {/* ── Pengaturan ──────────────────────────────── */}
        <Section title="Pengaturan">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white font-medium">Status Member</p>
              <p className="text-xs text-zinc-500 mt-0.5">Member aktif akan tampil di halaman publik grup</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => set('is_active', !form.is_active)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                  form.is_active ? 'bg-green-500' : 'bg-zinc-600'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                    form.is_active ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className={`text-xs font-medium w-14 ${form.is_active ? 'text-green-400' : 'text-zinc-500'}`}>
                {form.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </Section>

        </div>
      </form>
    </div>
  )
}
